import * as React from "react";
import { Box, TextField, Autocomplete, createFilterOptions, Typography, Chip } from "@mui/material";
import { connect } from "react-redux";
import { serviceProviderActions as actions } from "../../actions";
import { spFullNameSelector, serviceProvidersSortedSelector } from "../../reducers/serviceProvider";
import { t } from "../../components/translate";
import { getSpeciality } from "../../reducers/serviceTags";
import { ServiceProvider } from "../../interfaces/ServiceProvidersInterface";
import { IServiceTag } from "../../interfaces/ServiceTagsInterface";
import { canUseSpecialities } from "./ServiceproviderShow";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";

interface ServiceProvidersWithDisplaySpeciality extends ServiceProvider {
  displaySpeciality: string;
}

interface AutocompleteOptionsType {
  label: string;
  value: number;
  serviceProvider: ServiceProvidersWithDisplaySpeciality;
}

const processSpecialties = (
  sps: Array<ServiceProvider>,
  tags: IServiceTag[]
): ServiceProvidersWithDisplaySpeciality[] =>
  sps.map((sp) => {
    let { speciality } = sp;

    if (canUseSpecialities(sp.user?.userGroups?.[0]?.name ?? "", sp.specialities)) {
      speciality = getSpeciality(tags, sp.specialities[0]) || "";
    } else {
      speciality = sp.speciality || "";
    }

    return {
      ...sp,
      displaySpeciality: speciality
    };
  });

const optionsForSelect = ({
  serviceProviders = [],
  limitBy = [],
  tags = [],
  withSpAssign = false
}: {
  serviceProviders: Array<ServiceProvider>;
  limitBy?: Array<number>;
  tags: Array<IServiceTag>;
  withSpAssign?: boolean;
}): AutocompleteOptionsType[] => {
  const filterdServiceProviders = limitBy.length
    ? serviceProviders.filter((sp) => limitBy.includes(sp.id))
    : serviceProviders;

  if (withSpAssign && !limitBy.length) {
    return [];
  }

  const spsWithDisplaySpeciality = processSpecialties(filterdServiceProviders, tags);

  return spsWithDisplaySpeciality.map((sp) => ({
    label: spFullNameSelector(sp),
    value: sp.id,
    serviceProvider: sp
  }));
};

const filter = createFilterOptions<AutocompleteOptionsType>();

const formatValue = (
  spId: string | number,
  serviceProviders: ServiceProvider[] = [],
  tags: IServiceTag[] = []
): AutocompleteOptionsType | null => {
  const sP = serviceProviders?.find((sp) => sp.id === Number(spId));
  if (!sP) return null;

  // Keep the same speciality logic as options list (important)
  let speciality = "";
  if (canUseSpecialities(sP.user?.userGroups?.[0]?.name ?? "", sP.specialities)) {
    speciality = getSpeciality(tags, sP.specialities?.[0]) || "";
  } else {
    speciality = sP.speciality || "";
  }

  return {
    label: spFullNameSelector(sP),
    value: sP.id,
    serviceProvider: { ...sP, displaySpeciality: speciality }
  };
};

interface Props {
  resourceCentreId: number;
  serviceProviderId?: number;
  serviceProviderIds?: number[];
  serviceProviders: Array<ServiceProvider>;
  placeholder?: string;
  label?: string;
  limitBy?: Array<number>;
  onChange: (spIds: number[] | number | null, sp?: ServiceProvider) => void;
  loadServiceProviders: (v: number) => void;
  disabled?: boolean;
  isClearable?: boolean;
  serviceTags: Array<IServiceTag>;
  hideLabel?: boolean;
  disableDefaultError?: boolean;
  withSpAssign?: boolean;
  setShowServiceCommissionPopup?: React.Dispatch<React.SetStateAction<boolean>>;
  allowMultiSelect?: boolean;
}

export const ServiceProviderAutoSelect = ({
  resourceCentreId,
  serviceProviderId,
  serviceProviderIds = [],
  serviceProviders,
  placeholder = "",
  label,
  limitBy,
  onChange,
  loadServiceProviders,
  disabled = false,
  isClearable = false,
  hideLabel = false,
  serviceTags: tags,
  disableDefaultError,
  withSpAssign = false,
  setShowServiceCommissionPopup,
  allowMultiSelect = false
}: Props): React.JSX.Element | null => {
  const rc = useCurrentResourceCentre();
  const requireSpInService = rc?.settings.billingSettings.requireSpInService;

  let hasError =
    requireSpInService && !(serviceProviderId || (serviceProviderIds?.length ?? 0) > 0);

  if (!allowMultiSelect) {
    hasError = !serviceProviderId;
  }

  const optionsForSelectMemoized = React.useMemo(
    () =>
      optionsForSelect({ serviceProviders: serviceProviders || [], limitBy, tags, withSpAssign }),
    [serviceProviders, limitBy, tags, withSpAssign]
  );

  React.useEffect(() => {
    if ((!serviceProviders || !serviceProviders.length) && resourceCentreId) {
      loadServiceProviders(resourceCentreId);
    }
  }, [loadServiceProviders, resourceCentreId, serviceProviders]);

  const selectedSPsValue = React.useMemo(() => {
    if (!allowMultiSelect) return [];
    return (serviceProviderIds || [])
      .map((id) => formatValue(id, serviceProviders, tags))
      .filter(Boolean) as AutocompleteOptionsType[];
  }, [allowMultiSelect, serviceProviderIds, serviceProviders, tags]);

  const selectedSPValue = React.useMemo(() => {
    if (allowMultiSelect) return null;
    if (!serviceProviderId) return null;
    return formatValue(serviceProviderId, serviceProviders, tags) as AutocompleteOptionsType | null;
  }, [allowMultiSelect, serviceProviderId, serviceProviders, tags]);

  if (!serviceProviders) return null;

  return (
    <>
      <Autocomplete
        multiple={allowMultiSelect}
        id="combo-box-demo"
        data-testmation="serviceProviderSelector"
        options={optionsForSelectMemoized}
        value={allowMultiSelect ? selectedSPsValue : selectedSPValue}
        disableClearable={!isClearable}
        disabled={disabled}
        filterSelectedOptions
        onChange={(_, newValue) => {
          if (allowMultiSelect && Array.isArray(newValue)) {
            if (newValue.some((val) => val.value === -1)) {
              setShowServiceCommissionPopup?.(true);
              return;
            }
            onChange(newValue.map((v) => v.value));
            return;
          }

          if (!allowMultiSelect && !Array.isArray(newValue)) {
            onChange(newValue?.value || null, newValue?.serviceProvider || undefined);
          }
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          if (withSpAssign) {
            filtered.push({
              label: "Add service provider with commission",
              value: -1,
              serviceProvider: {
                firstName: "",
                lastName: "",
                title: ""
              }
            });
          }
          return filtered;
        }}
        isOptionEqualToValue={(option, value) => {
          if (!option || !value) return false;
          return option.value === value.value;
        }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(allowMultiSelect && {
          renderTags: (value: AutocompleteOptionsType[], getTagProps) =>
            value.map((option, index) => (
              <Chip
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...getTagProps({ index })}
                key={option.value}
                label={option.label}
                size="small"
                sx={{ maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis" }}
              />
            ))
        })}
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            error={!disableDefaultError && hasError}
            variant="outlined"
            fullWidth
            label={hideLabel ? "" : label || t("serviceProvider")}
            placeholder={placeholder || t("serviceProvider")}
            slotProps={{
              inputLabel: { shrink: true }
            }}
          />
        )}
        renderOption={(props, option) => {
          const speciality = option.serviceProvider?.displaySpeciality;
          return option.serviceProvider.id ? (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <li {...props} key={option.value}>
              {spFullNameSelector(option.serviceProvider)}
              <Box component="span" fontSize="small" sx={{ opacity: 0.6 }}>
                {speciality ? `, ${speciality}` : ""}
              </Box>
            </li>
          ) : (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <li {...props} key={option.value}>
              <Typography fontWeight={600} color="primary" fontSize={12}>
                {option.label}
              </Typography>
            </li>
          );
        }}
      />
    </>
  );
};

export default connect(
  (state: any) => ({
    serviceProviders: serviceProvidersSortedSelector(state),
    resourceCentreId: state.userContext.resourceCentreId,
    userPermissionGroup: state.userContext.userCreds.userGroups[0],
    serviceTags: state.serviceTags.collection
  }),
  (dispatch) => ({
    loadServiceProviders: (id) =>
      dispatch(actions.getResourceCentreServiceProviders({ resourceCentreId: id }))
  })
)(ServiceProviderAutoSelect);
